import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import { Button, Popconfirm, Select, Space, Table, Tag, notification } from 'antd';
import Column from 'antd/lib/table/Column';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import settings from '../../settings';
import {
  ONBOARDING_STAGE_KEYS,
  OnboardingTaskRoles,
  PartnerOnboardingStages,
  TaskInputTypes,
  allOnboardingTaskTemplatesQuery,
  deleteOnboardingTaskTemplateMutation,
  extractAllOnboardingTaskTemplates,
} from '../partner-onboarding/constants';
import CreateOnboardingTaskTemplateModal from './CreateOnboardingTaskTemplateModal';
import EditOnboardingTaskTemplateModal from './EditOnboardingTaskTemplateModal';

function ListOnboardingTaskTemplatesPage() {
  const filters = useRef({});
  const sortBy = useRef([
    { key: 'stageNumber', order: 'ASC' },
    { key: 'order', order: 'ASC' },
  ]);

  const {
    error,
    loading,
    data: templates,
    hasNextPage,
    next,
    refetch,
  } = useManyRemoteM1({
    query: allOnboardingTaskTemplatesQuery,
    extract: extractAllOnboardingTaskTemplates,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId: 'listOnboardingTaskTemplatesPage',
  });

  // `error` was destructured and never used, so a failed query looked like an empty
  // list of templates.
  useEffect(() => {
    if (error) {
      notification.error({ message: 'Error', description: error.message });
    }
  }, [error]);

  const [deleteTemplate] = useMutation(deleteOnboardingTaskTemplateMutation);
  const [deleting, setDeleting] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: settings.pageSize });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = useCallback(
    async (_id) => {
      setDeleting(_id);
      try {
        await deleteTemplate({ variables: { _id } });
        notification.success({ message: 'Deleted' });
        refetch();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
      setDeleting(null);
    },
    [deleteTemplate, refetch],
  );

  return (
    <CSPage containerStyle={{ maxWidth: 'unset' }} title="Onboarding Task Templates">
      <CSPageHeader title="Onboarding Task Templates" />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Filter by stage"
          style={{ width: 180 }}
          onChange={(val) => {
            filters.current = { ...filters.current, stageNumber: val };
            refetch();
          }}
        >
          {ONBOARDING_STAGE_KEYS.map((s) => (
            <Select.Option key={s} value={s}>
              {PartnerOnboardingStages[s]?.label}
            </Select.Option>
          ))}
        </Select>
        <Button type="primary" onClick={() => setCreating(true)}>
          Add Task Template
        </Button>
      </Space>
      <Table
        dataSource={templates}
        loading={loading}
        rowKey="_id"
        pagination={{ ...pagination, total: templates?.length }}
        onChange={(p) => setPagination(p)}
        scroll={{ x: 900 }}
      >
        <Column
          title="Stage"
          dataIndex="stageNumber"
          key="stageNumber"
          render={(val) => {
            const s = PartnerOnboardingStages[val];
            return s ? <Tag color={s.color}>{s.label}</Tag> : val;
          }}
          width={160}
        />
        <Column title="Order" dataIndex="order" key="order" width={80} />
        <Column title="Task" dataIndex="taskName" key="taskName" />
        <Column
          title="Input Type"
          dataIndex="inputType"
          key="inputType"
          render={(val) => TaskInputTypes[val]?.label || val}
          width={100}
        />
        <Column
          title="Assigned Role"
          dataIndex="assignedRole"
          key="assignedRole"
          render={(val) => OnboardingTaskRoles[val]?.label || val}
          width={110}
        />
        <Column title="SLA (days)" dataIndex="daysToComplete" key="daysToComplete" width={100} />
        <Column
          title="Active"
          dataIndex="isActive"
          key="isActive"
          render={(val) => (val ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>)}
          width={90}
        />
        <Column
          title="Actions"
          key="actions"
          width={140}
          render={(_, record) => (
            <Space>
              <Button size="small" onClick={() => setEditing(record)}>
                Edit
              </Button>
              {/* Deleting a template changes the completion computation for every
                  in-flight onboarding, so it gets the same guard as every other
                  destructive action in this feature. */}
              <Popconfirm
                title="Delete this task template?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDelete(record._id)}
              >
                <Button danger size="small" loading={deleting === record._id}>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      {hasNextPage && (
        <Button onClick={next} loading={loading}>
          Load more
        </Button>
      )}
      <CreateOnboardingTaskTemplateModal
        visible={creating}
        onCancel={() => setCreating(false)}
        onSuccess={() => {
          setCreating(false);
          refetch();
        }}
      />
      <EditOnboardingTaskTemplateModal
        record={editing}
        onCancel={() => setEditing(null)}
        onSuccess={() => {
          setEditing(null);
          refetch();
        }}
      />
    </CSPage>
  );
}

export default ListOnboardingTaskTemplatesPage;
